<template>
  <div v-if="showModule" class="activity-detail-module">
    <div class="activity-detail-container">
      <div class="activity-detail-content" @wheel.stop>
        <div v-if="loading" class="activity-detail-loading">
          <div class="activity-detail-skeleton-list" aria-hidden="true">
            <article
              v-for="index in 3"
              :key="index"
              class="activity-detail-card activity-detail-card-skeleton surface-card shadow-1"
            >
              <div class="activity-detail-card-header">
                <div class="activity-detail-employee">
                  <Skeleton shape="circle" size="3rem" />
                  <Skeleton width="9rem" height="1rem" borderRadius="999px" />
                </div>

                <Skeleton width="7rem" height="0.85rem" borderRadius="999px" />
              </div>

              <div class="activity-detail-grid">
                <div v-for="fieldIndex in 6" :key="fieldIndex" class="activity-detail-field">
                  <Skeleton width="5rem" height="0.8rem" borderRadius="999px" />
                  <Skeleton width="100%" height="1rem" borderRadius="999px" />
                </div>
              </div>
            </article>
          </div>
        </div>

        <div v-else-if="hasError" class="activity-detail-state">
          <EmptyState
            :title="t('wkt.client.activity-detail.error-state.title')"
            :description="t('wkt.client.activity-detail.error-state.description')"
          />
          <Button
            class="activity-detail-retry"
            :label="t('wkt.client.activity-detail.actions.retry')"
            @click="loadActivityDetail"
          />
        </div>

        <div v-else-if="logs.length === 0" class="activity-detail-state">
          <EmptyState
            :title="t('wkt.client.activity-detail.empty-state.title')"
            :description="t('wkt.client.activity-detail.empty-state.description')"
          />
        </div>

        <div v-else class="activity-detail-list">
          <article
            v-for="log in logs"
            :key="log.id ?? `${log.activityId}-${log.createDate}`"
            class="activity-detail-card surface-card shadow-1"
          >
            <div class="activity-detail-card-header">
              <div class="activity-detail-employee">
                <img
                  :src="getProfileAssetUrl(log.profilePicture)"
                  :alt="getEmployeeName(log)"
                  class="activity-detail-avatar"
                />
                <span class="activity-detail-employee-name">
                  {{ getEmployeeName(log) }}
                </span>
              </div>

              <span class="activity-detail-created-at">
                {{ formatLongDateTime(log.createDate ?? '', true) }}
              </span>
            </div>

            <div class="activity-detail-grid">
              <div class="activity-detail-field">
                <span class="activity-detail-label">
                  {{ t("wkt.client.activity-detail.activity-log.start-date") }}
                </span>
                <span class="activity-detail-value">
                  {{ formatLongDateTime(log.activityStart ?? '', true) }}
                </span>
              </div>

              <div class="activity-detail-field">
                <span class="activity-detail-label">
                  {{ t("wkt.client.activity-detail.activity-log.end-date") }}
                </span>
                <span class="activity-detail-value">
                  {{ formatLongDateTime(log.activityFinish ?? '', true) }}
                </span>
              </div>

              <div class="activity-detail-field">
                <span class="activity-detail-label">
                  {{ t("wkt.client.activity-detail.activity-log.description") }}
                </span>
                <span class="activity-detail-value">
                  {{ log.description || "—" }}
                </span>
              </div>

              <div class="activity-detail-field">
                <span class="activity-detail-label">
                  {{ t("wkt.client.activity-detail.activity-log.duration") }}
                </span>
                <span class="activity-detail-value">
                  {{ formatDuration(log) }}
                </span>
              </div>

              <div class="activity-detail-field">
                <span class="activity-detail-label">
                  {{ t("wkt.client.activity-detail.activity-log.activity-type") }}
                </span>
                <span class="activity-detail-value">
                  {{ log.activityTypeDescription || "—" }}
                </span>
              </div>

              <div v-if="log.projectName" class="activity-detail-field">
                <span class="activity-detail-label">
                  {{ t("wkt.client.activity-detail.activity-log.project") }}
                </span>
                <span class="activity-detail-value">
                  {{ log.projectName }}
                </span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import type {
  FullActivityLogDTO,
  FullActivityUpdateDetailDTO,
} from "@wkt/api/activityDetailDTO";
import { worktimeApi } from "@wkt/api/worktime";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { useAlert } from "@wkt/composables/useAlert";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";
import { useUtils } from "@wkt/composables/useUtils";

const { t } = useI18n();
const { showAlert } = useAlert();
const { formatLongDateTime } = useDateFormatter();
const { getProfileAssetUrl } = useUtils();

const showModule = ref(false);
const loading = ref(false);
const hasError = ref(false);
const logs = ref<FullActivityLogDTO[]>([]);
const currentActivityId = ref<number | null>(null);

function isActivityDetailPayload(payload: unknown): payload is FullActivityUpdateDetailDTO {
  return !!payload && typeof payload === "object" && ("logs" in payload || "activity" in payload);
}

function parseActivityId(candidate: unknown): number | null {
  if (typeof candidate === "number" && Number.isFinite(candidate)) {
    return candidate;
  }

  if (typeof candidate === "string" && candidate.trim().length > 0) {
    const parsed = Number.parseInt(candidate, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function resolveActivityId(payload?: unknown): number | null {
  const directValue = parseActivityId(payload);
  if (directValue !== null) {
    return directValue;
  }

  if (payload && typeof payload === "object" && "activityId" in payload) {
    const nestedValue = parseActivityId((payload as { activityId?: unknown }).activityId);
    if (nestedValue !== null) {
      return nestedValue;
    }
  }

  const hash = window.location.hash || "";
  const match = hash.match(/(?:[;?&])activity-id=([^;&]+)/i);
  if (!match?.[1]) {
    return null;
  }

  return parseActivityId(decodeURIComponent(match[1]));
}

function applyDetail(detail?: FullActivityUpdateDetailDTO | null): void {
  logs.value = detail?.logs ?? [];
  hasError.value = false;
  loading.value = false;
}

function getEmployeeName(log: FullActivityLogDTO): string {
  const lastname = (log.lastname || "").trim();
  const firstname = (log.firstname || "").trim();

  if (lastname && firstname) {
    return `${lastname}, ${firstname}`;
  }

  return lastname || firstname || "—";
}

function formatDuration(log: FullActivityLogDTO): string {
  if (!log.activityStart || !log.activityFinish) {
    return "00:00";
  }

  const start = new Date(log.activityStart).getTime();
  const finish = new Date(log.activityFinish).getTime();

  if (Number.isNaN(start) || Number.isNaN(finish) || finish < start) {
    return "00:00";
  }

  const totalMinutes = Math.floor((finish - start) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function loadActivityDetail(): void {
  if (!currentActivityId.value) {
    logs.value = [];
    hasError.value = true;
    loading.value = false;
    return;
  }

  loading.value = true;
  hasError.value = false;

  worktimeApi.getActivityDetail(
    currentActivityId.value,
    (response) => {
      applyDetail(response);
    },
    (error) => {
      console.error("Error loading activity detail:", error);
      logs.value = [];
      hasError.value = true;
      loading.value = false;
      showAlert({
        text: t("wkt.client.common.error.try_again_later"),
        color: "error",
      });
    }
  );
}

function onReveal(payload?: unknown): void {
  showModule.value = true;

  if (isActivityDetailPayload(payload)) {
    applyDetail(payload);
    currentActivityId.value =
      parseActivityId(payload.activity?.id) ??
      parseActivityId(payload.logs?.[0]?.activityId) ??
      currentActivityId.value;
    return;
  }

  currentActivityId.value = resolveActivityId(payload) ?? currentActivityId.value;
  loadActivityDetail();
}

function refreshModule(): void {
  showModule.value = true;
  currentActivityId.value = resolveActivityId() ?? currentActivityId.value;
  loadActivityDetail();
}

defineExpose({
  open: onReveal,
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.activity-detail-module {
  width: 100%;
  min-height: calc(100vh - 120px);
  padding: 0 0.75rem 2rem;
  box-sizing: border-box;
}

.activity-detail-container {
  width: 100%;
}

.activity-detail-content {
  max-width: 1120px;
  margin: 0 auto;
}

.activity-detail-loading,
.activity-detail-state {
  min-height: 16rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.activity-detail-retry {
  margin-top: 1rem;
}

.activity-detail-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activity-detail-skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.activity-detail-card {
  border-radius: 18px;
  padding: 1.25rem;
}

.activity-detail-card-skeleton {
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.activity-detail-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(var(--primary-rgb), 0.12);
}

.activity-detail-employee {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  min-width: 0;
}

.activity-detail-avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.activity-detail-employee-name {
  color: var(--primary-color);
  font-size: 1.05rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-detail-created-at {
  color: #5f6b7a;
  font-size: 0.95rem;
  text-align: right;
}

.activity-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
}

.activity-detail-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.activity-detail-label {
  font-size: 0.92rem;
  color: #5f6b7a;
  font-weight: 600;
}

.activity-detail-value {
  font-size: 0.98rem;
  color: #243344;
  line-height: 1.45;
  word-break: break-word;
}

@media (max-width: 768px) {
  .activity-detail-module {
    padding-inline: 0.25rem;
  }

  .activity-detail-card {
    padding: 1rem;
  }

  .activity-detail-card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .activity-detail-created-at {
    text-align: left;
  }

  .activity-detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
